import type { Event, EventHint } from '@sentry/types';

import { scopedLogger } from './scopedLogger';

const initWidgetFailure = /^Unable to init widget/g;

const addOptionalFingerprints = (event: Event) => {
  if (
    event.exception &&
    event.exception.values &&
    event.exception.values.length > 0
  ) {
    const errorMessage = event.exception.values[0].value;
    if (errorMessage && errorMessage.match(initWidgetFailure)) {
      event.fingerprint = ['Type: WidgetInit'];
    }
  }
};

export const beforeErrorLogSend = (
  event: Event,
  hint?: EventHint,
): Event | null => {
  addOptionalFingerprints(event);

  if (process.env.NODE_ENV !== 'production' && hint) {
    // eslint-disable-next-line no-console
    console.error(
      `[Sentry captured] ${hint.originalException || hint.syntheticException}`,
    );

    return null; // this drops the event and nothing will be send to sentry
  }

  return event;
};

const logger = scopedLogger(beforeErrorLogSend);
export const errorLogging = (): typeof logger => logger;

export interface Information {
  tags?: Map<string, string>;
  context?: Map<string, any>;
}

export const tagLogs = function <
  T extends { [key in K]: string },
  K extends string,
>(property: K, obj: T): void {
  errorLogging().setTag(property, obj[property]);
};

export const captureException = (
  exception: Error,
  information: Information = {},
): void => {
  errorLogging().withScope((scope) => {
    if (information.tags) {
      information.tags.forEach((val, key) => scope.setTag(key, val));
    }

    if (information.context) {
      information.context.forEach((val, key) => scope.setExtra(key, val));
    }

    errorLogging().captureException(exception);
  });
};

export const captureMessage = (
  message: string,
  information: Information = {},
): void => {
  errorLogging().withScope((scope) => {
    if (information.tags) {
      information.tags.forEach((val, key) => scope.setTag(key, val));
    }

    if (information.context) {
      information.context.forEach((val, key) => scope.setExtra(key, val));
    }

    errorLogging().captureMessage(message);
  });
};
